import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, OnDestroy, HostListener, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { CanComponentDeactivate } from 'src/app/providers/can-deactivate.guard';
import { GlobalGuardService } from 'src/app/providers/global-guard.service';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { FormChangeTracker } from 'src/app/shared/utils/form-change-tracker.util';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

import { AvatarUploadModalComponent } from '../../avatar-modal/avatar-upload-modal.component';
import { ImageViewerComponent } from '../../avatar-modal/image-viewer/image-viewer.component';
import { CityData } from '../city-data';

interface AddressOption {
  value: string;
  label: string;
}

interface PersonalInfo {
  code: string;
  name: string;
  unifiedNumber: string;
  city: string;
  website: string | null;
  address: string;
  description?: string;
  avatarId?: string;
  avatarFile: any;
}

@Component({
  selector: 'app-personal-tenant',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzSpinModule,
    NzGridModule,
    NzIconModule,
    NzDividerModule,
    NzDatePickerModule,
    NzToolTipModule,
    AvatarUploadModalComponent,
    ImageViewerComponent,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './personal-tenant.component.html',
  styleUrls: ['./personal-tenant.component.less']
})
export class PersonalTenantComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private readonly fb = inject(FormBuilder);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly http = inject(HttpService);
  private readonly imageService = inject(ImageService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly globalGuardService = inject(GlobalGuardService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  tenantId: string = '';
  infoForm!: FormGroup;
  loading = false;
  uploading = false;
  companyImageUrl = '';
  avatarUrl: string = '';
  isAvatarModalVisible = false;
  tempAvatarFile: any;

  // 表單變更檢測相關屬性
  private formChangeTracker?: FormChangeTracker;
  hasUnsavedChanges = false;
  addressOptions: any[] = CityData;

  imageViewerVisible = false;

  get showUnsavedStatus(): boolean {
    return this.hasUnsavedChanges;
  }

  ngOnInit(): void {
    this.globalGuardService.registerComponent(this);
    this.route.params.subscribe(params => {
      this.tenantId = this.resolveTenantId(params);
      if (this.tenantId) {
        this.initForm();
        this.loadPersonalInfo();
        this.setupFormChangeDetection();
      } else {
        this.message.error(this.i18n.fanyi('tenant.personal.tenant-id-empty'));
      }
    });
  }

  ngOnDestroy(): void {
    this.formChangeTracker?.stop();
    this.globalGuardService.unregisterComponent();
  }

  private initForm(): void {
    this.infoForm = this.fb.group({
      code: ['', [Validators.required]],
      name: ['', [Validators.required]],
      unifiedNumber: [''],
      website: [''],
      address: [''],
      city: [''],
      description: [''],
      avatarId: [''],
      avatarFile: ['', [Validators.required]]
    });
  }

  private resolveTenantId(params?: Record<string, string>): string {
    const routeTenantId =
      params?.['id'] ?? this.route.snapshot.paramMap.get('id') ?? this.route.parent?.snapshot.paramMap.get('id') ?? this.tenantId;
    return String(routeTenantId ?? '').trim();
  }

  // 修改 loadAvatarImage 方法
  loadAvatarImage(avatarId: string): void {
    this.imageService
      .loadImage(avatarId, 'M')
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(imageUrl => {
        if (imageUrl) {
          this.companyImageUrl = imageUrl;
          this.avatarUrl = imageUrl;
          // 暂时禁用表单变更监听
          this.formChangeTracker?.stop();

          this.infoForm.patchValue({
            avatarFile: this.companyImageUrl
          });

          // 重新设置变更检测
          this.resetChangeDetection();
          this.setupFormChangeDetection();
        } else {
          console.error('tenant.personal.load-avatar-failed');
          this.companyImageUrl = '';
        }
      });
  }

  // 修改 loadPersonalInfo 方法中的逻辑
  private loadPersonalInfo(): void {
    if (!this.tenantId) {
      this.message.error(this.i18n.fanyi('tenant.personal.tenant-id-empty'));
      return;
    }

    this.loading = true;
    this.http
      .post(`${ConstantUtil.ADMIN_TENANT_V1_PERSONAL_DETAIL}/${this.tenantId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success && res.data) {
            const data = res.data;
            // 移除結束時間，增加描述字段
            // 暂时禁用表单变更监听
            this.formChangeTracker?.stop();

            this.infoForm.patchValue({
              code: data.code ?? '',
              name: data.name ?? '',
              unifiedNumber: data.unifiedNumber ?? '',
              website: data.website,
              address: data.address ?? '',
              city: data.city ?? '',
              description: data.description ?? '',
              avatarId: data.avatarId ?? ''
            });

            if (data.avatarId) {
              this.loadAvatarImage(data.avatarId);
            } else {
              // 如果没有头像，直接重置变更检测
              this.resetChangeDetection();
              this.setupFormChangeDetection();
            }
          } else {
            showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.personal.load-personal-info-failed'));
          }
        },
        error: error => {
          this.message.error(this.i18n.fanyi('tenant.personal.load-personal-info-failed'));
          console.error('Load personal info error:', error);
        }
      });
  }
  showAvatarUploadModal(): void {
    this.isAvatarModalVisible = true;
  }

  handleAvatarUpload(event: any): void {
    this.uploading = true;
    const reader = new FileReader();
    this.tempAvatarFile = new File([event.blob], 'avatar.png', { type: 'image/png' });
    reader.onload = (e: any) => {
      this.uploading = false;
      this.companyImageUrl = e.target.result;
      this.avatarUrl = this.companyImageUrl;
      this.infoForm.patchValue({
        avatarFile: this.companyImageUrl
      });
      this.handleAvatarModalCancel();
      this.hasUnsavedChanges = this.formChangeTracker?.hasChanged() ?? false;
    };
    reader.readAsDataURL(this.tempAvatarFile);
  }

  handleAvatarModalCancel(): void {
    this.isAvatarModalVisible = false;
  }

  removeImage(): void {
    this.companyImageUrl = '';
    this.avatarUrl = '';
    this.infoForm.patchValue({
      avatarFile: ''
    });
    this.message.success(this.i18n.fanyi('tenant.personal.image-deleted'));
    this.hasUnsavedChanges = this.formChangeTracker?.hasChanged() ?? false;
  }

  saveInfo(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.infoForm.valid) {
        if (!this.tenantId) {
          this.message.error(this.i18n.fanyi('tenant.personal.tenant-id-empty'));
          reject(new Error(this.i18n.fanyi('tenant.personal.tenant-id-empty')));
          return;
        }

        this.loading = true;

        const formData = this.infoForm.value;
        const processedData = {
          ...this.normalizeFormData(formData),
          id: this.tenantId
        };
        console.log('[personal-update] request payload', {
          url: ConstantUtil.ADMIN_TENANT_V1_PERSONAL_UPDATE,
          tenantId: this.tenantId,
          processedData,
          hasTempAvatarFile: !!this.tempAvatarFile
        });
        this.http
          .postFormData(ConstantUtil.ADMIN_TENANT_V1_PERSONAL_UPDATE, processedData, this.tempAvatarFile)
          .pipe(
            finalize(() => {
              this.loading = false;
              this.cdr.detectChanges();
            })
          )
          .subscribe(res => {
            if (res.success) {
              this.message.success(this.i18n.fanyi('tenant.personal.save-success'));
              // 保存成功后重置变更状态
              this.resetChangeDetection();
              // 清除临时文件
              this.tempAvatarFile = null;
              resolve();
            } else {
              const errorMessage = res.msg ?? this.i18n.fanyi('tenant.personal.save-failed');
              showFallbackHttpError(this.message, res.msg, errorMessage);
              reject(new Error(errorMessage));
            }
          });
      } else {
        this.showFormErrors();
        reject(new Error(this.i18n.fanyi('tenant.personal.form-validation-failed')));
      }
    });
  }

  // 設置表單變更檢測
  private setupFormChangeDetection(): void {
    this.formChangeTracker?.stop();
    this.formChangeTracker = new FormChangeTracker(this.infoForm, () => this.infoForm.getRawValue());
    this.formChangeTracker.start(hasChanged => {
      this.hasUnsavedChanges = hasChanged;
    });
  }

  // 檢查表單是否有變更
  private hasFormChanged(currentValue: any): boolean {
    void currentValue;
    return this.formChangeTracker?.hasChanged() ?? false;
  }

  // 重置變更檢測狀態
  private resetChangeDetection(): void {
    this.formChangeTracker?.reset();
    this.hasUnsavedChanges = false;
  }

  // 頁面離開前的確認提示
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.hasUnsavedChanges) {
      $event.returnValue = this.i18n.fanyi('tenant.personal.unsaved-changes-warning');
    }
  }

  // 檢查是否可以離開頁面
  canDeactivate(): Promise<boolean> {
    if (this.hasUnsavedChanges) {
      return new Promise(resolve => {
        this.modal.confirm({
          nzTitle: this.i18n.fanyi('tenant.personal.changes-not-saved'),
          nzContent: this.i18n.fanyi('tenant.personal.save-changes-confirmation'),
          nzOkText: this.i18n.fanyi('tenant.personal.save'),
          nzCancelText: this.i18n.fanyi('tenant.personal.leave-without-saving'),
          nzIconType: 'exclamation-circle',
          nzClosable: false,
          nzOnOk: () => {
            // 用户选择保存，先保存数据再离开
            this.saveInfo()
              .then(() => {
                resolve(true);
              })
              .catch(() => {
                resolve(false);
              });
          },
          nzOnCancel: () => {
            this.hasUnsavedChanges = false;
            this.resetChangeDetection();
            resolve(true);
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  private showFormErrors(): void {
    // 标记所有字段为已触摸，以显示验证错误
    Object.values(this.infoForm.controls).forEach(control => {
      if (control.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
    this.message.error(this.i18n.fanyi('tenant.personal.fill-required-fields'));
  }
  openImageViewer(imageUrl: string): void {
    this.imageViewerVisible = true;
  }

  closeImageViewer(): void {
    this.imageViewerVisible = false;
  }

  private normalizeFormData(formData: PersonalInfo): PersonalInfo {
    return {
      ...formData,
      website: this.normalizeOptionalUrl(formData.website)
    };
  }

  private normalizeOptionalUrl(value: string | null): string {
    const trimmedValue = value?.trim() ?? '';
    return trimmedValue ? trimmedValue : '';
  }
}
